import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useIsFetching } from '@tanstack/react-query';
import * as stylex from '@stylexjs/stylex';
import '@astryxdesign/core/theme/tokens.stylex';
import { colorVars, spacingVars } from '@astryxdesign/core/theme/tokens.stylex';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { useToast } from '@astryxdesign/core/Toast';
import { useMediaQuery } from '@astryxdesign/core/hooks';
import { useFlip } from '@qronstat/ui';
import { desktopQuery, mobileQuery } from '../app/layout';
import { useLayoutMutations, useScreenLayout } from '../api/layouts';
import { CustomizeBar, GridTile, TileControls, tileHeight, useTileDrag, useTileResize } from './Customize';
import { applyLayout, defaultPlacement, displayWidth, isDefaultPlacement, mergeLayout, moveItem, screenUnits } from './layout';
import type { LayoutMode, Placed } from './layout';
import { useApp } from '../app/context';
import { text } from '../model/meta';
import { useSavedItems } from '../api/saved';
import { ScreenProvider, useScreen } from './ScreenContext';
import { ScreenHeader } from './ScreenHeader';
import type { ScreenDef } from './types';
import type { FilterNode } from '@qronstat/shared';
function ChangeProgress() {
  const {
    state
  } = useScreen();
  const fetching = useIsFetching({
    queryKey: ['q']
  }) + useIsFetching({
    queryKey: ['objects']
  });
  const signature = JSON.stringify([state.period, state.filters, state.realOnly, state.compare, state.timeMode]);
  const last = useRef(signature);
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (last.current === signature) return;
    last.current = signature;
    setActive(true);
    const t = setTimeout(() => setActive(false), 15_000);
    return () => clearTimeout(t);
  }, [signature]);
  useEffect(() => {
    if (active && fetching === 0) setActive(false);
  }, [active, fetching]);
  if (!active || fetching < 2) return null;
  return <div className="xixxii4 x17y0mx6 x13vifvy x36qwtl xfo81ep x47corl xb3r6kr" role="progressbar" aria-busy="true">
      <div className="x5yr21d xz84dc7 x1ewilqj x1vlha0e xr7ufhj xa4qsjk x4hg4is" />
    </div>;
}
function DefaultFilterSet({
  screenId
}: {
  screenId: string;
}) {
  const {
    search,
    update
  } = useScreen();
  const saved = useSavedItems({
    kind: 'filter_set',
    screen: screenId
  });
  const applied = useRef(false);
  useEffect(() => {
    if (applied.current || !saved.data) return;
    applied.current = true;
    if (search.f !== undefined || search.all !== undefined) return;
    const def = saved.data.items.find(i => i.isDefaultForScreen && i.isOwner);
    if (!def) return;
    const d = def.definition as {
      filters?: FilterNode[];
      realOnly?: boolean;
    };
    update({
      f: d.filters?.length ? d.filters : undefined,
      all: d.realOnly === false ? true : undefined
    });
  }, [saved.data, search, update]);
  return null;
}
function ScreenBody() {
  const intl = useIntl();
  const toast = useToast();
  const {
    screen
  } = useScreen();
  const isPhone = useMediaQuery(mobileQuery);
  const isDesktop = useMediaQuery(desktopQuery);
  const viewport = isPhone ? 'phone' : isDesktop ? 'desktop' : 'tablet';
  const mode: LayoutMode = isPhone ? 'phone' : 'wide';
  const units = useMemo(() => screenUnits(screen.widgets), [screen.widgets]);
  const stored = useScreenLayout(screen.id);
  const storedLayout = stored.data?.layout ?? null;
  const {
    save,
    reset
  } = useLayoutMutations(screen.id);
  const saved = useMemo(() => applyLayout(units, storedLayout, mode), [units, storedLayout, mode]);
  const [draft, setDraft] = useState<{
    mode: LayoutMode;
    placed: Placed[];
  } | null>(null);
  const editing = draft !== null && draft.mode === mode ? draft.placed : null;
  const [lastMoved, setLastMoved] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const {
    meta,
    locale
  } = useApp();
  const labelOf = (p: Placed) => {
    if (p.unit.item) return text(meta?.metric(p.unit.item.metric)?.title, locale) || p.unit.item.metric;
    return p.unit.def.titleText ?? intl.formatMessage({
      id: p.unit.def.title
    });
  };
  const update = (change: (list: Placed[]) => Placed[]) => setDraft(current => current ? {
    ...current,
    placed: change(current.placed)
  } : current);
  const move = (id: string, to: number) => {
    setLastMoved(id);
    update(list => moveItem(list, list.findIndex(p => p.unit.id === id), to));
  };
  const resize = (id: string, size: {
    w: number;
    h: number;
  }) => update(list => list.map(p => p.unit.id === id ? {
    ...p,
    w: size.w,
    h: size.h
  } : p));
  const order = useMemo(() => (editing ?? []).map(p => p.unit.id), [editing]);
  const drag = useTileDrag(gridRef, order, move);
  const sizing = useTileResize(gridRef, mode, resize);
  useFlip(gridRef, '[data-widget-id]', order.join('|'));
  const movedIndex = editing && lastMoved ? editing.findIndex(p => p.unit.id === lastMoved) : -1;
  const moved = movedIndex >= 0 ? editing?.[movedIndex] : undefined;
  const announcement = moved ? intl.formatMessage({
    id: 'layout.moved'
  }, {
    title: labelOf(moved),
    n: movedIndex + 1,
    count: editing?.length ?? 0
  }) : '';
  const finish = () => {
    if (!editing) return;
    const next = mergeLayout(storedLayout, units, mode, editing);
    const done = {
      onSuccess: () => {
        setDraft(null);
        toast({
          body: intl.formatMessage({
            id: 'layout.saved'
          })
        });
      },
      onError: () => toast({
        body: intl.formatMessage({
          id: 'layout.saveFailed'
        }),
        type: 'error' as const
      })
    };
    if (next) save.mutate(next, done);else reset.mutate(undefined, done);
  };
  const list = editing ?? saved.filter(p => !p.hidden);
  return <>
      <ChangeProgress />
      <DefaultFilterSet screenId={screen.id} />
      <ScreenHeader title={screen.titleText ?? intl.formatMessage({
      id: screen.title
    })} customize={{
      active: editing !== null,
      onToggle: () => setDraft(d => d && d.mode === mode ? null : {
        mode,
        placed: saved
      })
    }} />
      {editing ? <CustomizeBar isPhone={isPhone} canReset={!isDefaultPlacement(editing, units, mode)} saving={save.isPending || reset.isPending} announcement={announcement} onReset={() => setDraft({
      mode,
      placed: defaultPlacement(units, mode)
    })} onCancel={() => setDraft(null)} onSave={finish} /> : null}
      <div ref={gridRef} className="x1n2onr6 xrvj5dj x17m5ync x1qjc9v5 xtx9w7w xdqyycr x1na6nto">
        {list.map((p, index) => <GridTile key={p.unit.id} placed={p} width={displayWidth(p.w, viewport)} eager={index < eagerTiles} label={labelOf(p)} editing={editing !== null} active={drag.dragging === p.unit.id || sizing.resizing === p.unit.id}>
            {editing ? <TileControls placed={p} label={labelOf(p)} index={index} count={editing.length} mode={mode} measure={() => tileHeight(gridRef.current, p.unit.id)} onDragStart={e => drag.start(p.unit.id, e)} onResizeStart={e => sizing.start(p, e)} onMove={to => move(p.unit.id, to)} onResize={size => resize(p.unit.id, size)} onToggleHidden={() => update(items => items.map(q => q.unit.id === p.unit.id ? {
          ...q,
          hidden: !q.hidden
        } : q))} /> : null}
          </GridTile>)}
      </div>
    </>;
}
const eagerTiles = 8;
export function ScreenPage({
  screen
}: {
  screen: ScreenDef;
}) {
  const intl = useIntl();
  const {
    can
  } = useApp();
  if (!can(screen.permission)) {
    return <EmptyState title={intl.formatMessage({
      id: 'access.denied'
    })} description={intl.formatMessage({
      id: 'access.deniedHint'
    })} />;
  }
  return <ScreenProvider screen={screen} key={screen.id}>
      <ScreenBody />
    </ScreenProvider>;
}